import Tooltip from '@mui/material/Tooltip';
import { faBullhorn, faCartArrowDown, faCheck, faComments, faDisplay, faDroplet, faExclamationTriangle, faLayerGroup, faLocationDot, faMagnifyingGlass, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { MapBrowserEvent, View } from 'ol';
import Map from 'ol/Map';
import Attribution from 'ol/control/Attribution.js';
import MousePosition from 'ol/control/MousePosition.js';
import ScaleLine from 'ol/control/ScaleLine.js';
import Zoom from 'ol/control/Zoom';
import ZoomToExtent from 'ol/control/ZoomToExtent.js';
import React, { lazy, useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import Dropdown from '../../components/common/Dropdown.tsx';
import setTitle from '../../components/common/SetTitle.tsx';
import EFASHeaderFullScreenComponent from '../../components/common/header/EFASHeaderFullScreenComponent.tsx';
import GLOFASHeaderFullScreenComponent from '../../components/common/header/GLOFASHeaderFullScreenComponent.tsx';
import LoadingSpinner from '../../components/loadingSpinner/LoadingSpinner.tsx';
import { ExternalWMSConfig, FloatingWindowDimensions, LoginState, OutletIProps, WebSettingPreset } from '../../components/sitebase/Base.types.tsx';
import IS_EFAS from '../../components/sitebase/Environment.ts';
import { FeatureInfoQueryResult, FeatureInfoStore, LegendData, MapActiveLayer, TimelineStateI, WMSInfo, WMSLayer, WMSMap } from './Map.types.ts';
import { LAYER_ENCODE_SEPARATOR, MapPresenter } from './MapPresenter.ts';
import CenteredModal from './components/CenteredModal.tsx';
import FeatureInfo from './components/FeatureInfo.tsx';
import InteractiveMapPopup from './components/InteractiveMapPopup.tsx';
import Legend from './components/Legend.tsx';
import { MapLink } from './components/MapLink.tsx';
import ForecastDate from './components/layerselector/ForecastDate.tsx';
import LayerSelector from './components/layerselector/LayerSelector.tsx';
import Timeline from './components/timeline/Timeline.tsx';
import ActiveNotificationsWidget from './components/widgets/ActiveNotificationsWidget.tsx';
import ExternalStateModalWidget from './components/widgets/ExternalStateModalWidget.tsx';
import ExternalWMSWidget from './components/widgets/ExternalWMSWidget.tsx';
import GFMWidget from './components/widgets/GFMWidget.tsx';
import MapSettings from './components/widgets/MapSettings.tsx';
import MapWidget from './components/widgets/MapWidget.tsx';
import { MapWidgetPosition } from './components/widgets/MapWidget.types';
import MessagesWidget from './components/widgets/MessagesWidget.tsx';
import ModalWidget from './components/widgets/ModalWidget.tsx';
import PresetWidget from './components/widgets/PresetWidget.tsx';
import ReportMissedEventForm from './components/widgets/ReportMissedEventForm.tsx';
import VersionAnnouncementButtonWidget from './components/widgets/VersionAnnouncementButtonWidget.tsx';
import MapTutorial from './components/MapTutorial.tsx';
import WMSList from './components/wmslist/WMSList.tsx';
import { WMSListPresentation } from './components/wmslist/WMSList.types';
import { useInteractivePopup } from './hooks/useInteractivePopup.ts';
import { useFeatureInfo } from './hooks/useFeatureInfo.ts';
import { useGfmWidget as useGfm } from './hooks/useGfmWidget.ts';
import { useMapLayers } from './hooks/useMapLayers.ts';
import { useMapSettings } from './hooks/useMapSettings.ts';
import { useMissedEvents } from './hooks/useMissedEvents.ts';
import { usePresets } from './hooks/usePresets.ts';
import { useTOS as useTOS } from './hooks/useTOS.ts';
import { useVersionMessage } from './hooks/useVersionWelcome.ts';
import { EFAS_HOME_EXTENT, epsg3857 } from './maputils.ts';
import { MapSettingsService } from './services/MapSettingsService.ts';

import { useShareState } from './hooks/useShareState.ts';
import { useMinWidthQuery } from '../../hooks/useMediaQuery.ts';
import { LayerSelectorViews } from './components/layerselector/LayerSelector.types';

import gfmIcon from './static/GFM.svg';
import addwms from './static/addwms.png';

import './css/map.css';
import { useFeedbacks } from './hooks/useFeedbacks.ts';

const SearchWidget = lazy(() => import('./components/widgets/SearchWidget.tsx'));
const LayerSearchWidget = lazy(() => import('./components/widgets/LayerSearchWidget.tsx'));
export const ACTIVENOTIF_PARAM_NAME = "activenotif";
export const LAYER_SELECTOR_PARAM_NAME = "lview";

// Land-focused global home view for GLOFAS.
const glofasHomeSouthWest = epsg3857({ lon: -180, lat: -55.9875 });
const glofasHomeNorthEast = epsg3857({ lon: 180, lat: 83.629167 });
const GLOFAS_HOME_EXTENT: [number, number, number, number] = [
    glofasHomeSouthWest[0],
    glofasHomeSouthWest[1],
    glofasHomeNorthEast[0],
    glofasHomeNorthEast[1],
];

export function MapComponent(props: { presenter: MapPresenter, height?: string, width?: string }) {
    const mapElement = React.useRef<HTMLDivElement>(null);
    const tooltipEl = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (mapElement.current) props.presenter.map.setTarget(mapElement.current as HTMLElement);
    }, [props.presenter]);

    return <>
        <div ref={mapElement} className="map-container" style={{ height: props.height || "calc(100vh - 5.4em)", width: props.width || "100vw" }} id="map">
            <div ref={tooltipEl} className='map-tooltip' id="info"></div>
        </div>
    </>;
};

export default function MapAppFullScreen() {
    const context = useOutletContext<OutletIProps>();
    const { refreshUser } = context;
    const [refreshed, setRefreshed] = useState<boolean>(false);

    // todo make this smarter - if coming from another place in the app then refreshing can be avoided
    // *if MapLink did the refreshing*. but if it's a fresh load we still want to do this so that the
    // user info is finished being fetched and prevent rendering the UI as anonymous, then updating all
    // components with the logged in state. just looks better although functionality wise it's kind of the same

    useEffect(() => {
        refreshUser();
        setRefreshed(true);
        return () => {
            refreshUser();
        };
    }, [refreshUser]);

    if (!IS_EFAS && context.user.loggedIn === LoginState.Failed) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.assign(`${window.location.origin}/accounts/login/?next=${next}`);
        return <LoadingSpinner />;
    }

    return refreshed && context.user.loggedIn !== LoginState.InProgress && <MapAppV3 /> || <LoadingSpinner />;
}

export function MapAppV3() {
    const context = useOutletContext<OutletIProps>();
    const { refreshUser } = context;
    const anonymousUser = context.user.loggedIn === LoginState.Failed;
    const [activePopup, startActivePopupTransition] = useTransition();
    const SMALL_DISPLAY = useMinWidthQuery();

    const share = useShareState(context);

    const remote = new MapSettingsService(context.user.token);
    const tos = useTOS(context.user.web_settings.tos_accepted, remote);

    const popupElement = useRef<HTMLDivElement>(null);

    // Created exactly once: useState runs the initializer on the first render only, and the
    // setter is deliberately not taken so the presenter can never be replaced - replacing it
    // would detach it from the live OpenLayers map.
    const [presenter] = useState<MapPresenter>(() => {
        const customCenterIcon = document.createElement("i");
        customCenterIcon.innerHTML = '<svg data-prefix="fas" data-icon="globe" class="svg-inline--fa fa-globe" role="img" viewBox="0 0 512 512" aria-hidden="true"><path fill="currentColor" d="M351.9 280l-190.9 0c2.9 64.5 17.2 123.9 37.5 167.4 11.4 24.5 23.7 41.8 35.1 52.4 11.2 10.5 18.9 12.2 22.9 12.2s11.7-1.7 22.9-12.2c11.4-10.6 23.7-28 35.1-52.4 20.3-43.5 34.6-102.9 37.5-167.4zM160.9 232l190.9 0C349 167.5 334.7 108.1 314.4 64.6 303 40.2 290.7 22.8 279.3 12.2 268.1 1.7 260.4 0 256.4 0s-11.7 1.7-22.9 12.2c-11.4 10.6-23.7 28-35.1 52.4-20.3 43.5-34.6 102.9-37.5 167.4zm-48 0C116.4 146.4 138.5 66.9 170.8 14.7 78.7 47.3 10.9 131.2 1.5 232l111.4 0zM1.5 280c9.4 100.8 77.2 184.7 169.3 217.3-32.3-52.2-54.4-131.7-57.9-217.3L1.5 280zm398.4 0c-3.5 85.6-25.6 165.1-57.9 217.3 92.1-32.7 159.9-116.5 169.3-217.3l-111.4 0zm111.4-48C501.9 131.2 434.1 47.3 342 14.7 374.3 66.9 396.4 146.4 399.9 232l111.4 0z"></path></svg>';

        return new MapPresenter(
            new Map({
                // ...mapSettings,
                view: new View({
                    projection: 'EPSG:3857',
                    center: share.state.c
                        ? share.state.c.split(",").map(parseFloat)
                        : sessionStorage.getItem('map:center')
                            ? sessionStorage.getItem('map:center')!.split(",").map(parseFloat)
                            : epsg3857({ lon: 15, lat: 50 }),
                    zoom: share.state.z
                        ? parseFloat(share.state.z)
                        : sessionStorage.getItem('map:zoom')
                            ? parseFloat(sessionStorage.getItem('map:zoom')!)
                            : 5,
                    minZoom: 3,
                    maxZoom: 18,
                }),
                controls: [
                    new Zoom({ className: 'ol-zoom ol-bottom-left' }),
                    new ZoomToExtent({
                        className: 'ol-zoom-extent ol-bottom-left',
                        label: customCenterIcon,
                        extent: IS_EFAS ? EFAS_HOME_EXTENT : GLOFAS_HOME_EXTENT,
                    }),
                    new ScaleLine({
                        // className: 'ol-scale-line ol-bottom-left',
                        // bar: true,
                        units: 'metric',
                        steps: 4,
                        // minWidth: 140,
                    }),
                    new MousePosition({
                        className: 'ol-unselectable ol-control ol-cursor-position',
                        projection: 'EPSG:4326',
                        coordinateFormat: (coordinate?: number[]) => {
                            if (!coordinate) {
                                return 'Lat -, Lon -';
                            }
                            const [lon, lat] = coordinate;
                            return `${lat.toFixed(3)}, ${lon.toFixed(3)}`;
                        },
                        placeholder: 'Lat -, Lon -',
                    }),
                    new Attribution({
                        collapsible: true,
                        className: 'mx-auto ol-attribution ol-map-attribution',
                    }),
                ],
            })
        );
    });

    // whether the initial state from the URL has been loaded into the map
    const [stateLoaded, setStateLoaded] = useState<boolean>(false);
    // for UI reactions to the forecast changing
    const [forecastDate, setForecastDate] = useState<string>(share.state.forecast || "");
    // for the presenter - the ref is available in the onOlSingleClick callback
    const forecastDateRef = useRef<string>(share.state.forecast || "");
    const lastForecastDate = useRef<string>("");
    const loadingCounter = useRef(0);

    const layers = useMapLayers(presenter, remote, context.user.web_settings, share, forecastDate);
    const featureInfo = useFeatureInfo(presenter, remote, context.user.web_settings);
    const interactive = useInteractivePopup(presenter, remote, share);
    const feedbacks = useFeedbacks(presenter);
    const missedEvents = useMissedEvents(presenter);
    const gfm = useGfm(presenter);
    const settings = useMapSettings(presenter, remote, context.user.web_settings);
    const presets = usePresets(presenter, remote, context.user.web_settings.presets as WebSettingPreset, settings.selectedBaseMap);
    const versionMessage = useVersionMessage(context.user.web_settings.accepted_welcome_message, forecastDate, remote);

    // priority order is: layers in the URL/HREF, layers from the user settings, no layers
    const [paramListActiveLayers, _] = useState<string>(share.state.layers || context.user.web_settings.active_layers.join(",") || '');
    const [paramListActiveMapPins, __] = useState<{ [forecast: string]: FeatureInfoStore }>(context.user.web_settings.active_map_pins || {});

    const [legend, setLegend] = useState<LegendData | null>(null);
    const [wmsMap, setWmsMap] = useState<WMSMap>({});

    const [legendDimensions, setLegendDimensions] = useState<FloatingWindowDimensions>(context.user.web_settings.legend_window_dimensions);

    const [wmsListPresentation, setWmsListPresentation] = useState<WMSListPresentation>(context.user.web_settings.wms_list_presentation || WMSListPresentation.ICONS);
    const [externalWms, setExternalWms] = useState<ExternalWMSConfig[]>(context.user.web_settings.external_wms_list);

    const [mapLoading, setMapLoading] = useState<boolean>(false);
    const [layerSearchVisible, setLayerSearchVisible] = useState<boolean>(false);
    const [searchVisible, setSearchVisible] = useState<boolean>(false);

    // UI-icons/messages states, i.e. they actively are used to show an element on the UI
    const [cartCount, setCartCount] = useState<number>(context.user.cartlength);
    const [timelineState, setTimelineState] = useState<TimelineStateI | undefined>(undefined);
    // End of UI states /////////////////////////////////////////////////////////////////

    // used to prevent spamming forecastDate events
    const isSettingForecastDate = useRef<boolean>(false);

    // controls whether the forecast time gating is enforced - EFAS latest forecasts are not publicly available
    // they become public only 30d after their initial publication
    const [forecastEmbargoActive, setForecastEmbargoActive] = useState<boolean>(true);

    const escapeCallback = useCallback((event: KeyboardEvent) => {
        const hasModifierKey = event.ctrlKey || event.metaKey || event.altKey || event.shiftKey;

        if (event.key === 'Escape') {
            featureInfo.setWindowVisible(false);
            setLayerSearchVisible(false);
            setSearchVisible(false);
        }
        if ((event.key === 'f' || event.key === 'F') && !hasModifierKey) {
            const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
            const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select'
                || (document.activeElement as HTMLElement)?.isContentEditable;
            if (!isEditable) {
                event.preventDefault();
                setSearchVisible(v => !v);
            }
        }
        if ((event.key === 't' || event.key === 'T') && !hasModifierKey) {
            const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
            const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select'
                || (document.activeElement as HTMLElement)?.isContentEditable;
            if (!isEditable) {
                event.preventDefault();
                setLayerSearchVisible(v => !v);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- featureInfo is the per-render hook result object; adding it would recreate this keydown handler on every render
    }, []);

    useEffect(() => {
        document.body.addEventListener('keydown', escapeCallback);
        return () => {
            document.body.removeEventListener('keydown', escapeCallback);
        };
    }, [escapeCallback]);

    const onOlSingleClick = useRef((evt: MapBrowserEvent) => {
        if (presenter.isDrawing()) { return };
        if (presenter.hitIconLayer(evt)) {
            return;
        }

        // if any interactive layers are toggled first check if the user has hit one of them
        const feature = presenter.showInteractivePopup(evt, popupElement.current as HTMLDivElement);
        if (feature) {
            startActivePopupTransition(() => {
                interactive.setFeature(feature);
            });
            return;
        } else {
            // if not then proceed to do the usual get feature info
            const fd = moment.utc(forecastDateRef.current).format("YYYY-MM-DD HH:mm");
            presenter.getFeatureInfo(evt, fd).then((result: FeatureInfoQueryResult) => {
                featureInfo.set(result.store);
                // Open the window only when THIS query added a valid feature. The store
                // returned by the presenter retains pins from earlier queries (closing with
                // close_removes_point=false keeps them), so using its size here would wrongly
                // reopen a hidden window after failed or empty clicks. When nothing was added
                // we leave the current visibility untouched, so a visible window stays visible.
                if (result.added) {
                    featureInfo.setWindowVisible(true);
                }
                remote.updateUserWebSettings({ active_map_pins: { [moment.utc(forecastDateRef.current).format("YYYY-MM-DDTHH:mm") + "Z"]: result.store } });
            }).catch((e) => { console.debug("message", e) });
        }
    });

    const onOlMoveEnd = useRef(() => {
        // get center of map and set it in the search params
        const view = presenter.map.getView();
        const center = view.getCenter();
        const zoom = view.getZoom();
        if (center && zoom) {
            share.updateCenter(center, zoom);
            sessionStorage.setItem('map:center', center.join(','));
            sessionStorage.setItem('map:zoom', String(zoom));
            // window.history.replaceState(undefined, undefined, `#${center.join(",")}/${zoom}`)
        }
    });

    // const copyHref = useCallback(() => {
    //     window.navigator.clipboard.writeText(window.location.href);
    //     toast("Map URL copied to clipboard. You can paste it to share the current map view.")
    // }, []);

    const initialWms = useRef([
        IS_EFAS ?
            {
                name: "EFAS",
                // url: `https://${window.location.hostname}/api/fms/wmsproxy/mapserver/?service=WMS&`,
                url: `https://${window.location.hostname}/api/wms/?service=WMS`,
                // proxy: `https://${window.location.hostname}/api/fms/getcapabilities/?url=`,
                params: '&request=GetCapabilities',
                capabilitiesFormat: 'text/xml',
                // capabilitiesFormat: 'application/json',
                featureInfoFormat: 'text/html',
                forecastLayerInfoFormat: 'html/point',
                forecastLayers: ['mapserver:ReportingPoints', 'mapserver:ReportingPointsNotifications', 'mapserver:Eric_v2', 'mapserver:Eric_BigTriangles'],
                latestForecastLayer: 'mapserver:date',
                baseWms: true,
                version: '1.1.1',
            }
            // EFAS but through fastapi and with text/xml
            // {
            //     name: "EFAS",
            //     url: `https://${window.location.hostname}/api/fms/wmsproxy/mapserver/?service=WMS&`,
            //     params: '&request=GetCapabilities',
            //     capabilitiesFormat: 'text/xml',
            //     featureInfoFormat: 'text/html',
            //     forecastLayerInfoFormat: 'html/point',
            //     forecastLayers: ['ReportingPoints', 'ReportingPointsNotifications', 'Eric_v2', 'Eric_BigTriangles'],
            //     latestForecastLayer: 'ReportingPoints',
            //     baseWms: true,
            //     version: '1.1.1',
            // }
            :
            {
                name: "GLOFAS",
                url: `${window.location.origin}/proxy/?srv=ows&SERVICE=WMS`,
                params: '&VERSION=1.3.0&REQUEST=GetCapabilities',
                capabilitiesFormat: 'text/xml',
                featureInfoFormat: 'text/html',
                latestForecastLayer: window.location.origin.includes("canary") ? 'RPG_U_GLOFAS_3_3-aifs' : 'RPG_U_GLOFAS_3_3',
                baseWms: true,
                version: '1.3.0',
            }
    ]);

    // map action implementations, these should be doing as LITTLE as possible
    // and majority of the logic should be in the presenter which interacts with the OpenLayers API
    // and adjusts the state of the objects accordingly, before returning it here to be updated inside react
    // If the presenter is ever called in the callback it must be added in the dependency array

    const onAddWms = useCallback((wmsInfo: WMSInfo) => {
        console.debug("Adding WMS", wmsInfo);
        // functional update: capabilities for several servers resolve back-to-back, and each
        // must add to the latest map rather than to the one captured when this callback was made
        startActivePopupTransition(() => {
            setWmsMap(prev => ({ ...prev, [wmsInfo.url]: wmsInfo }));
        });
    }, []);

    const onClearAllFeatures = useCallback(() => {
        console.debug("Clearing features");
        const newFeatureInfos = presenter.removeAllFeatureInfos();
        featureInfo.set(newFeatureInfos);
        remote.updateUserWebSettings({ active_map_pins: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- featureInfo and remote are per-render objects; adding them would recreate this callback on every render
    }, []);

    const onSetForecastDate = useCallback(async (date: string) => {
        if (isSettingForecastDate.current) {
            console.debug('Forecast date update skipped; another update in progress');
            return;
        }
        console.debug("Setting forecast date", date);
        const fixedDate = moment.utc(date).format("YYYY-MM-DDTHH:mm") + "Z";
        isSettingForecastDate.current = true;
        try {
            presenter.setForecastDate(fixedDate);

            if (anonymousUser && IS_EFAS) {
                setForecastEmbargoActive(presenter.forecastIsWithinEmbargo(fixedDate, lastForecastDate.current));
            }

            if (interactive.position !== MapWidgetPosition.HIDDEN) {
                const active = await presenter.showActiveNotifications(fixedDate, lastForecastDate.current);
                interactive.setPoints(active);
            }
            if (feedbacks.visible) {
                await feedbacks.show(fixedDate);
            }
            setForecastDate(fixedDate);
            share.updateForecast(fixedDate);
            forecastDateRef.current = fixedDate;
            // actionHistory.current.push(`Set forecast date to ${date}`);
        } finally {
            isSettingForecastDate.current = false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- interactive/feedbacks/share are per-render hook result objects; adding them would recreate this callback on every render. `anonymousUser` is also untracked: it only changes if `context.refreshUser()` resolves a different login state
    }, [lastForecastDate, interactive.position, interactive.filters, feedbacks.visible]);

    const onSetForecastDateToLayers = useCallback(async (layers: MapActiveLayer[], date: string) => {
        console.debug("Setting forecast date to layers", layers, date);
        presenter.setForecastDateToLayers(layers, date);
    }, [presenter]);

    const onShowLegend = useCallback((layer: MapActiveLayer) => {
        console.debug("Showing legend", layer);
        presenter.showLegend(layer, forecastDate).then((legend) => setLegend(legend));
        // actionHistory.current.push(`Showed legend for layer ${layer.id}`);
    }, [forecastDate, presenter]);

    // updates a WMS dimension on the OpenLayers Layer WMS object
    const onUpdateDimension = useCallback((layer: MapActiveLayer, dimension: string, value: string, forecastDate?: string) => {
        presenter.updateDimension(layer, dimension, value, forecastDate);
        // actionHistory.current.push(`Updated dimension ${dimension} for layer ${layer.id} to ${value}`);
    }, [presenter]);

    const onSetCartCounter = useCallback((count: number) => {
        console.debug("Setting cart count", count);
        setCartCount(count);
    }, []);

    const onSearchResultClicked = useCallback((point: { lat: string, lon: string }) => {
        console.debug("Search result clicked", point);
        presenter.zoomToPoint({ lat: parseFloat(point.lat), lon: parseFloat(point.lon) });
        // actionHistory.current.push(`Search result clicked ${point}`);
    }, [presenter]);

    const onLegendDimensionChange = (dimensions: FloatingWindowDimensions) => {
        console.debug("Legend dimension change", dimensions);
        setLegendDimensions(dimensions);
        remote.updateUserWebSettings({ legend_window_dimensions: dimensions });
    }

    const onWmsListPresentationChange = (presentation: WMSListPresentation) => {
        console.debug("WMS List presentation change", presentation);
        setWmsListPresentation(presentation);
        remote.updateUserWebSettings({ wms_list_presentation: presentation });
        // actionHistory.current.push(`Changed WMS list presentation to ${presentation}`)
    };

    const onSetLayerSelectorView = (view: LayerSelectorViews) => {
        console.debug("Setting layer selector view", view);
        share.updateLayerSelectorView(view);
        remote.updateUserWebSettings({ layer_selector_view: view });
        // actionHistory.current.push(`Set layer selector view to ${view}`);
    }

    // could be in useMapLayers but needs to be dependent on the wmsMap and it's the _only_ action dependent on it
    const reloadAllLayers = (layersToLoad: string) => {
        // load active layersToLoad from the search params
        const layersToAdd: [wms: string, group: string, layerObj: WMSLayer, visible?: boolean | string, opacity?: number][] = [];
        layersToLoad.split(",").forEach(async (layerEntry: string) => {
            if (!layerEntry) {
                return;
            }

            let wms: string | undefined;
            let group: string | undefined;
            let layer: string | undefined;
            let visible: boolean | string | undefined;
            let opacityValue: number | undefined;

            try {
                const payload = JSON.parse(decodeURIComponent(layerEntry));
                if (payload && typeof payload.id === "string") {
                    [wms, group, layer] = payload.id.split(LAYER_ENCODE_SEPARATOR);
                    visible = payload.visible;
                    opacityValue = Number.isFinite(payload.opacity) ? payload.opacity : undefined;
                }
            } catch {
                const [legacyWms, legacyGroup, legacyLayer, legacyVisible, legacyOpacityRaw] = layerEntry.split(LAYER_ENCODE_SEPARATOR);
                wms = legacyWms;
                group = legacyGroup;
                layer = legacyLayer;
                visible = legacyVisible;
                const legacyOpacity = legacyOpacityRaw !== undefined ? parseFloat(legacyOpacityRaw) : undefined;
                opacityValue = Number.isFinite(legacyOpacity) ? legacyOpacity : undefined;
            }

            if (wms && group && layer && Object.prototype.hasOwnProperty.call(wmsMap, wms)) {
                try {
                    const layerObj = wmsMap[wms].layers[group].find((l: WMSLayer) => l.Name === layer);
                    if (layerObj) {
                        layersToAdd.push([wms, group, layerObj, visible, opacityValue]);
                    }
                } catch (e) {
                    console.error(e);
                }
            }
        });
        layers.onAdd(layersToAdd);
    };

    const onSetExternalWms = (wmsList: ExternalWMSConfig[]) => {
        console.debug("Setting external WMS list", wmsList);
        setExternalWms(wmsList);
        remote.updateUserWebSettings({ external_wms_list: wmsList });
    };

    const onSetTimelineState = useCallback((state: TimelineStateI) => {
        console.debug("Setting timeline state", state);
        setTimelineState(state);
    }, [])

    const checkForNewerForecast = useCallback(async () => {
        try {
            const baseWmsConfig = initialWms.current[0];
            const wmsInfo = await presenter.loadCapabilities("0", baseWmsConfig, context.user.token, true);

            if (wmsInfo && wmsInfo.latestForecastDate) {
                const newLatestForecast = moment.utc(wmsInfo.latestForecastDate);

                if (newLatestForecast.isAfter(lastForecastDate.current)) {
                    toast.info(
                        <div>
                            <div className="mb-2">A newer forecast is available ({newLatestForecast.format('YYYY-MM-DD HH:mm')} UTC)</div>
                            <button
                                className="btn btn-sm btn-warning"
                                onClick={() => {
                                    // Update WMS capabilities with new forecast
                                    onAddWms(wmsInfo);
                                    // Update the last forecast date reference
                                    lastForecastDate.current = wmsInfo.latestForecastDate;
                                    // Switch to the new forecast date
                                    onSetForecastDate(wmsInfo.latestForecastDate);
                                    // Close the toast
                                    toast.dismiss();
                                }}
                            >
                                Load New Forecast
                            </button>
                        </div>,
                        {
                            position: 'top-center',
                            autoClose: false,
                            closeOnClick: false,
                            draggable: false,
                        }
                    );
                } else {
                    console.log("Already on the latest forecast");
                }
            }
        } catch (error) {
            console.debug("Error checking for newer forecast:", error);
            toast.error("Error checking for newer forecast", { position: 'top-center', autoClose: 3000 });
        }
    }, [context.user.token, onAddWms, onSetForecastDate, presenter]);

    useEffect(() => {
        onSetCartCounter(context.user.cartlength);
    }, [context.user.cartlength, onSetCartCounter]);

    useEffect(() => {
        if (!IS_EFAS) return;

        if (!context.user.token) {
            refreshUser();
        }
    }, [context.user.token, refreshUser]);

    useEffect(() => {
        if (!IS_EFAS) return;

        setCartCount(context.user.cartlength);
    }, [context.user.cartlength]);

    useEffect(() => {
        // keeps the presenter's copy of the view state current: the presenter interacts with the
        // OpenLayers API and manages the state value before returning it back to the callbacks
        // below to set it in THIS component
        presenter.syncViewState({
            layers: layers.active,
            featureInfos: featureInfo.data,
            wms: wmsMap,
            useMapMarkers: settings.useMapMarkers,
            forceMapserver: settings.forceMapserver,
        });

        console.debug("Active layers", layers.active);
    }, [layers.active, featureInfo, wmsMap, settings.useMapMarkers, settings.forceMapserver, presenter]);

    // initialise the capabilities and attach the OpenLayers callbacks
    useEffect(() => {
        setTitle("Map Viewer");

        for (const [order, server] of Object.entries(initialWms.current)) {
            presenter.loadCapabilities(order, server, context.user.token).then((wmsInfo: WMSInfo | void) => {
                if (wmsInfo) {
                    onAddWms(wmsInfo);
                }
            });
        }

        presenter.map.on('singleclick', onOlSingleClick.current);
        presenter.map.on('moveend', onOlMoveEnd.current);
        presenter.map.on('loadstart', () => {
            loadingCounter.current += 1;
            setMapLoading(true);
        });
        presenter.map.on('loadend', () => {
            loadingCounter.current -= 1;
            if (loadingCounter.current <= 0) setMapLoading(false);
        });

        // WMS independent initialisations
        if (context.user.web_settings.base_map) {
            presenter.setBaseMap(parseInt(context.user.web_settings.base_map));
        }
        if (!anonymousUser && (share.state.activenotif || context.user.web_settings.activenotif === "on"))
            interactive.show(forecastDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/init effect keyed on the user token; its other inputs (interactive/share/forecastDate/web_settings) are intentionally read once to attach OL callbacks and load capabilities
    }, [context.user.token]);

    useEffect(() => {
        for (const [order, server] of Object.entries(externalWms)) {
            presenter.loadCapabilities(order, server).then((wmsInfo: WMSInfo | void) => {
                if (wmsInfo) {
                    onAddWms(wmsInfo);
                }
            }).catch((e) => {
                console.error("Error fetching capabilities for ", server, e);
                toast.error("Error fetching capabilities for WMS " + server.name, { position: 'bottom-center' });
            });
        }
    }, [externalWms, presenter, onAddWms]);

    useEffect(() => {
        // Load application state from already available information, such as the active layers and latest forecast
        // make sure the initial WMS has been loaded and that the whole thing is ran only once
        // TODO to support external WMS we'd need to parse the paramListActiveLayers here and
        // then make sure it's WMS is loaded before processing it
        // Applied for anonymous users too: restricted layers stay disabled via the embargo check in the
        // layer list, and efas-backend's WMS proxy refuses/truncates restricted requests for anonymous users
        if (!stateLoaded && forecastDate !== "" && paramListActiveLayers && paramListActiveMapPins && featureInfo && wmsMap && Object.prototype.hasOwnProperty.call(wmsMap, initialWms.current[0].url)) {
            console.log("Loading initial application state from user web settings with active layers", paramListActiveLayers, "and forecast date", forecastDate, "and WMS map", wmsMap);
            reloadAllLayers(paramListActiveLayers);
            // flags the state as loaded so that this useEffect is not called again
            if (settings.rememberMapMarkers && forecastDate in paramListActiveMapPins) {
                const features = paramListActiveMapPins[forecastDate];
                featureInfo.set(features);
                presenter.loadFeatures(features);
                featureInfo.setWindowVisible(false);
            }
            setStateLoaded(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reloadAllLayers is a per-render helper and settings.rememberMapMarkers only gates the one-shot state load; the stateLoaded flag makes this effect idempotent
    }, [wmsMap, paramListActiveLayers, paramListActiveMapPins, forecastDate, featureInfo, stateLoaded]);

    useEffect(() => {
        if (lastForecastDate.current !== "") {
            return;
        }
        if (wmsMap && Object.entries(wmsMap).length > 0) {
            const baseWms = Object.values(wmsMap).find((wms) => wms.url === initialWms.current[0].url);
            if (baseWms) {
                lastForecastDate.current = baseWms.latestForecastDate;
                onSetForecastDate(forecastDate || baseWms.latestForecastDate);
            }
        }
    }, [wmsMap, forecastDate, onSetForecastDate]);

    // Check for newer forecasts every 30 minutes and on window focus
    // useEffect(() => {
    //     const handleWindowFocus = () => {
    //         checkForNewerForecast();
    //     };

    //     // Only set up the interval and focus listener if we have a valid forecast date
    //     if (forecastDate && !anonymousUser) {
    //         // Check every 10 minutes
    //         const intervalId = setInterval(checkForNewerForecast, 10 * 60 * 1000);

    //         // Check when window regains focus
    //         window.addEventListener('focus', handleWindowFocus);

    //         return () => {
    //             clearInterval(intervalId);
    //             window.removeEventListener('focus', handleWindowFocus);
    //         };
    //     }
    // }, [forecastDate, anonymousUser, checkForNewerForecast]);

    const messagesComponent = !anonymousUser ? <MessagesWidget context={context} setWelcomeMessageVisible={versionMessage.setVisible} /> :
        IS_EFAS && <VersionAnnouncementButtonWidget onClick={() => versionMessage.setVisible(true)} />;

    return <>
        <ToastContainer position='top-center' />
        {IS_EFAS ?
            <EFASHeaderFullScreenComponent user={context.user} extraButtons={
                <div className='d-flex flex-row me-1 my-auto'>
                    <div className='d-none d-sm-block'>
                        {messagesComponent}
                    </div>
                    {context.user.forecaster &&
                        <MapLink id="cart-button" to={'/cart'} role='button' className='btn d-flex flex-row fs-8'>
                            <FontAwesomeIcon className='m-auto' icon={faCartArrowDown} />
                            <span>
                                <span className='me-1'>Cart</span>
                            </span>
                            <div className='badge bg-danger my-auto ms-1'>{cartCount}</div>
                        </MapLink>}
                </div>
            } /> :
            <GLOFASHeaderFullScreenComponent user={context.user} extraButtons={
                <div className='d-none d-sm-flex flex-row me-1 my-auto'>
                    {messagesComponent}
                </div>} />}

        <div id="layer-and-widgets-toolbar" className='d-flex flex-column user-select-none'>
            {/* Layer selection */}
            {anonymousUser && SMALL_DISPLAY && <div className='d-flex fs-8 mx-auto my-auto border border-warning rounded mx-2 mt-1 mb-0 my-lg-auto'>
                <div className='mx-2'>Real-time forecasts of the past 30 days are restricted to <Link to="/become-efas-partner">EFAS partners<span className='text-dark'>.</span></Link></div>
            </div>}
            <div className='d-flex flex-row mt-0 ms-2'>
                <div id="tutorial-wms-list" className='d-flex flex-column'>
                    <WMSList
                        baseWms={initialWms.current[0].url}
                        wmsMap={wmsMap}
                        activeLayers={layers.active}
                        favourites={layers.favourites}
                        setFavouriteLayer={layers.onAddFavourite}
                        addOrRemoveLayer={layers.onAddOrRemove}
                        presentation={wmsListPresentation}
                        anonymousUser={anonymousUser}
                        forecastEmbargoActive={forecastEmbargoActive}
                        forecastDate={forecastDate}
                    />
                </div>
                {anonymousUser && !SMALL_DISPLAY && <div className='d-flex fs-8 mx-auto my-auto border border-warning rounded mx-2 mt-1 mb-0 my-lg-auto'>
                    <div className='mx-2'>Real-time forecasts of the past 30 days are restricted to <Link to="/become-efas-partner">EFAS partners<span className='text-dark'>.</span></Link></div>
                </div>}
                {IS_EFAS && !anonymousUser && !SMALL_DISPLAY &&
                    <div className='d-flex flex-row my-auto'>
                        <Tooltip title="Show active notifications" placement="bottom" arrow disableInteractive>
                            <div id='show-active-notifications' data-testid='show-active-notifications' className='btn' onClick={() => interactive.show(forecastDate)}
                                style={{ color: interactive.position !== MapWidgetPosition.HIDDEN ? "green" : "" }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                        </Tooltip>
                        <Tooltip title="Show feedbacks" placement="bottom" arrow disableInteractive>
                            <div id='show-feedbacks' className='btn' onClick={() => feedbacks.toggle(forecastDate)}
                                style={{ color: feedbacks.visible ? "orange" : "" }}>
                                <FontAwesomeIcon icon={faComments} />
                            </div>
                        </Tooltip>
                        <Tooltip title="Show missed events" placement="bottom" arrow disableInteractive>
                            <div id='show-missed-events' className='btn ms-1' onClick={missedEvents.toggle}
                                style={{ color: missedEvents.visible ? "green" : "" }}>
                                <FontAwesomeIcon icon={faBullhorn} />
                            </div>
                        </Tooltip>
                    </div>
                }

                {/* Right-side control group */}
                <div className='d-flex flex-row my-auto ms-auto'>
                    {/* Feature info button */}
                    {!featureInfo.windowVisible && Object.keys(featureInfo.data).length !== 0 &&
                        <Tooltip title="Open point information window" placement="bottom" arrow disableInteractive>
                            <button className="btn" onClick={() => featureInfo.setWindowVisible(true)}>
                                <FontAwesomeIcon icon={faLocationDot} />
                            </button>
                        </Tooltip>
                    }

                    {/* Report missed event - only for logged-in users */}
                    {!anonymousUser && IS_EFAS &&
                        <Tooltip title="Report missed event" placement="bottom" arrow disableInteractive>
                            <button className={`btn ${missedEvents.interactionIsActive && 'btn-outline-success'}`}
                                onClick={() => {
                                    if (missedEvents.interactionIsActive) {
                                        missedEvents.finishInteraction()
                                    } else {
                                        missedEvents.startInteraction();
                                    }
                                }}>
                                <FontAwesomeIcon icon={faDroplet} />
                            </button>
                        </Tooltip>
                    }

                    {/* GFM widget - hidden on small displays */}
                    {!anonymousUser && !SMALL_DISPLAY &&
                        <Tooltip title="Open GFM widget" placement="bottom" arrow disableInteractive>
                            <button id="tutorial-gfm" className='btn'
                                onClick={() => gfm.setPosition(gfm.position === MapWidgetPosition.HIDDEN ? MapWidgetPosition.BOTTOM_RIGHT : MapWidgetPosition.HIDDEN)}
                                style={{ color: gfm.position !== MapWidgetPosition.HIDDEN ? "green" : "" }}>
                                <img src={gfmIcon} alt="GFM Logo" style={{ height: "1.35rem", width: "1.35rem", verticalAlign: "0px" }} />
                            </button>
                        </Tooltip>
                    }

                    {/* Layer search button - always visible */}
                    <Tooltip title="Search layers (T)" placement="bottom" arrow disableInteractive>
                        <button
                            id="tutorial-layer-search"
                            className="btn"
                            onClick={() => setLayerSearchVisible(v => !v)}
                            style={{ color: layerSearchVisible ? 'var(--bs-primary)' : undefined }}
                        >
                            <FontAwesomeIcon icon={faLayerGroup} />
                        </button>
                    </Tooltip>

                    {/* Search button */}
                    <Tooltip title="Search location or station (F)" placement="bottom" arrow disableInteractive>
                        <button
                            id="tutorial-search"
                            className="btn"
                            onClick={() => setSearchVisible(v => !v)}
                            style={{ color: searchVisible ? 'var(--bs-primary)' : undefined }}
                        >
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                        </button>
                    </Tooltip>

                    {/* Search widget - always visible (essential) */}
                    <span style={{ display: 'contents' }}>
                        {/* <Suspense fallback={<div className="btn"></div>}> */}
                        <SearchWidget onResultClicked={onSearchResultClicked} forecastDate={forecastDate} token={context.user.token} visible={searchVisible} onClose={() => setSearchVisible(false)} />
                        {/* </Suspense> */}
                    </span>

                    {/* Layer search widget - triggered by button or pressing 'f' */}
                    <LayerSearchWidget
                        visible={layerSearchVisible}
                        onClose={() => setLayerSearchVisible(false)}
                        wmsMap={wmsMap}
                        activeLayers={layers.active}
                        onAddOrRemove={layers.onAddOrRemove}
                        forecastDate={forecastDate}
                        isForecaster={context.user.forecaster}
                        isDevUser={context.user.groups.includes('Development')}
                    />

                    {/* External WMS - hidden on small displays */}
                    {!SMALL_DISPLAY && !anonymousUser &&
                        <ModalWidget toggleButton={
                            <Tooltip title="Add external WMS" placement="bottom" arrow disableInteractive>
                                <a id="tutorial-add-wms" className="btn" role="button" aria-expanded="false">
                                    <img style={{ width: "25px" }} src={addwms} />
                                </a>
                            </Tooltip>
                        } title={''}>
                            <ExternalWMSWidget externalWms={externalWms} setExternalWms={onSetExternalWms} />
                        </ModalWidget>
                    }

                    {/* Presets - hidden on small displays */}
                    {!anonymousUser && !SMALL_DISPLAY &&
                        <Dropdown toggleButton={
                            <Tooltip title="Presets" placement="bottom" arrow disableInteractive>
                                <a id="preset-toggle" className="btn dropdown-toggle" role="button" aria-expanded="false">
                                    <FontAwesomeIcon icon={faDisplay} />
                                </a>
                            </Tooltip>
                        }
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            width="25rem"
                        >
                            <PresetWidget
                                onLoadPreset={(presetValue: string) => presets.onLoadPreset(
                                    presetValue,
                                    reloadAllLayers,
                                    onSetForecastDate,
                                    onSetLayerSelectorView
                                )}
                                onSavePreset={presets.onSavePreset}
                                onDeletePreset={presets.onDeletePreset}
                                shareState={share.state}
                                presets={presets.data}
                                stateToString={presets.stateToString} />
                        </Dropdown>
                    }

                    {/* Map settings - always visible (essential) */}

                    <MapSettings
                        activeNotificationWidgetPosition={interactive.position}
                        setActiveNotificationWidgetPosition={interactive.setPosition}
                        wmsListPresentation={wmsListPresentation}
                        onWmsListPresentationChange={onWmsListPresentationChange}
                        featureInfoPresentation={featureInfo.presentation}
                        onFeatureInfoPresentationChange={featureInfo.onPresentationChange}
                        settings={settings}
                        checkForNewerForecast={checkForNewerForecast}
                    />

                    {/* Tutorial help button */}
                    {!IS_EFAS && <MapTutorial
                        anonymousUser={anonymousUser}
                        isSmallDisplay={SMALL_DISPLAY}
                        isForecaster={!!context.user.forecaster}
                    // wmsReady={Object.keys(wmsMap).length > 0 && forecastDate !== ""}
                    />}
                </div>
            </div>
        </div>

        <MapComponent presenter={presenter} />

        {/* Wraps the ActiveNotificationPopup in a div so that React can correctly
        free the element even though it may be held by the OpenLayers "Overlay" object.
        Otherwise the app crashes
        */}
        <div style={{
            display: interactive.feature ? "block" : "none"
        }}>
            <InteractiveMapPopup ref={popupElement} id="popup"
                className="ol-popup fs-9"
                feature={interactive.feature}
                isPending={activePopup}
                onClose={interactive.closePopup} />
        </div>
        <MapWidget id="tutorial-layer-widget" position={settings.forecastWidgetPosition} title="Layers"
            style={{
                backgroundColor: "white",
                width: "20rem",
                overflow: "hidden",
                boxShadow: "0 0 6px rgba(0, 0, 0, 0.5)"
            }}>

            {share.state.lview !== LayerSelectorViews.TIME && <ForecastDate
                currentForecastDate={forecastDate}
                onSetForecastDate={onSetForecastDate}
                forecastIncrement={IS_EFAS ? 12 : 24}
                lastForecastDate={lastForecastDate.current}
                ignoreLatestForecastTime={settings.ignoreLatestForecastTime}
                disableTime={!IS_EFAS}
                disableTimeTooltip="GloFAS only provides daily forecasts" />}

            <LayerSelector
                currentForecastDate={forecastDate}
                view={share.state.lview as LayerSelectorViews}
                setView={onSetLayerSelectorView}
                onSetForecastDateToLayers={onSetForecastDateToLayers}
                activeLayers={layers.active}
                onRemoveLayer={layers.onRemove}
                onReorderLayer={layers.onReorder}
                onDragReorderLayer={layers.onDragReorder}
                onSendLayerToTop={layers.onSendToTop}
                onToggleLayer={layers.onToggle}
                onShowLegend={onShowLegend}
                onChangeOpacity={layers.onChangeOpacity}
                onUpdateDimension={onUpdateDimension}
                onToggleAllLayers={layers.onToggleAll}
                onRemoveAllLayers={layers.onRemoveAll}
                onClearAllFeatures={onClearAllFeatures}
                versionMessage={versionMessage && versionMessage.data ? versionMessage.data.version : ""}
                timelineState={timelineState}
                setTimelineState={onSetTimelineState}
                forecastEmbargoActive={anonymousUser && forecastEmbargoActive}
                opacityPresentation={settings.activeLayersOpacity}
                reorderingPresentation={settings.activeLayersReordering}
            />

        </MapWidget>
        <MapWidget position={gfm.position} title="GFM"
            style={{
                backgroundColor: "white",
                // border: "1px solid black",
                padding: "5px",
                width: "20rem",
                overflow: "hidden",
                boxShadow: "0 0 6px rgba(0, 0, 0, 0.5)"
            }}>
            <GFMWidget
                activeDrawing={gfm.isDrawing}
                onGfmShowAoi={gfm.showAoi}
                onGfmHideAoi={gfm.hideAoi}
                onGfmStartDrawAoi={gfm.startDrawAoi}
                onGfmClearDrawAoi={gfm.clearDrawAoi}
                onGfmCreateAoi={gfm.createAoi} />
        </MapWidget>
        <MapWidget position={share.state.lview === LayerSelectorViews.TIME && MapWidgetPosition.BOTTOM_FOOTER || MapWidgetPosition.HIDDEN}
            style={{
                // backgroundColor: "white",
                // border: "1px solid black",
                // padding: "5px",
                // margin: "10px 10px",
                // width: "56rem",
                overflow: "hidden",
                // boxShadow: "0 0 6px rgba(0, 0, 0, 0.5)"
            }}>
            <Timeline
                state={timelineState}
                activeLayers={layers.active}
                currentForecastDate={forecastDate}
                onSetForecastDate={onSetForecastDate}
                // forecastStep={IS_EFAS ? 12 : 24}
                lastForecastDate={lastForecastDate.current}
                onUpdateDimension={onUpdateDimension}
                ignoreLatestForecastTime={settings.ignoreLatestForecastTime}
            />
        </MapWidget>
        {
            !anonymousUser &&
            <MapWidget position={interactive.position} title="Notifications"
                style={{
                    backgroundColor: "white",
                    padding: "5px",
                    width: "20rem",
                    overflow: "hidden",
                    boxShadow: "0 0 6px rgba(0, 0, 0, 0.5)"
                }}>
                <ActiveNotificationsWidget
                    allNotifications={interactive.allPoints}
                    filteredNotifications={interactive.filteredPoints}
                    filters={interactive.filters}
                    onFilterChange={interactive.updateFilter}
                    onNotificationTitleClicked={(point: { lat: number, lon: number }) => presenter.zoomToPoint(point)}
                />
            </MapWidget>
        }
        <FeatureInfo
            presenter={presenter}
            feature={featureInfo}
            onSetCartCounter={onSetCartCounter}
            onClearAllFeatures={onClearAllFeatures}
            latestForecast={lastForecastDate.current}
            closeRemovesPoint={settings.closeRemovesPoint}
        />
        <Legend
            legend={legend}
            dimensions={legendDimensions}
            userLoggedIn={!anonymousUser}
            onDimensionChange={onLegendDimensionChange} />

        {
            IS_EFAS && tos.accepted && <CenteredModal
                open={versionMessage.visible}
                onClose={() => versionMessage.setVisible(false)}
                width={SMALL_DISPLAY ? "95vw" : "60vw"}
                title={versionMessage.data.title}
                maxContentHeight="70vh"
                contentClassName="fs-8"
                content={
                    <>
                        {versionMessage.data.body === "" ? <div><LoadingSpinner /> Loading latest announcement...</div> :
                            <div dangerouslySetInnerHTML={{ __html: versionMessage.data.body }}></div>}
                        <div>For more information please have a look at: <a href={versionMessage.data.link_url}>{versionMessage.data.link_url}</a></div>
                    </>
                }
                actions={
                    <>
                        <div className='btn btn-warning me-2' onClick={() => versionMessage.setVisible(false)}>OK</div>
                        <div className='btn btn-warning' onClick={() => versionMessage.onAccept()}>OK and don't show again</div>
                    </>
                }
            />
        }
        {
            IS_EFAS && !tos.accepted && <CenteredModal
                open={tos.visible}
                width={SMALL_DISPLAY ? "95vw" : "50vw"}
                borderTop="3px solid orange"
                contentClassName="fs-5"
                content={
                    <>
                        <strong>EFAS real time forecasts are only accessible for <a href="/become-efas-partner" target="_blank" rel="noopener noreferrer">EFAS Partners.</a> </strong>
                        <br />
                        <strong> Please accept the  <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> to proceed.</strong>
                    </>
                }
                actions={
                    <div className="padding d-flex flex-row justify-content-center">
                        <button onClick={tos.onOkClick} className="btn btn-warning">
                            <FontAwesomeIcon icon={faCheck} /> I accept
                        </button>
                        <button onClick={tos.onNoClick} className="btn btn-danger ms-2">
                            <FontAwesomeIcon icon={faTimes} /> I decline (redirects to home page)
                        </button>
                    </div>
                }
            />
        }
        {
            missedEvents.feature && <ExternalStateModalWidget
                title="Report a missed event"
                clickingOverlayClosesModal={false}
                showDefaultCloseButton={true}
                closeModalCallback={() => {
                    missedEvents.setModalVisible(false);
                    missedEvents.finishInteraction();
                }}
                visible={missedEvents.modalVisible}>
                <ReportMissedEventForm feature={missedEvents.feature as import("ol/Feature").default<import("ol/geom/Geometry").default>} closeForm={() => {
                    missedEvents.finishInteraction();
                }} />
            </ExternalStateModalWidget>
        }
        {
            mapLoading && <div style={{
                position: "absolute",
                top: "5rem",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2000,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                padding: "5px 15px",
                borderRadius: "20px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                pointerEvents: "none"
            }}>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                <span className="fs-7 fw-bold text-secondary">Loading...</span>
            </div>
        }
    </>;
};
